import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

// Configure worker once at module scope
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/* ============================================================
 * File extraction (unchanged)
 * ========================================================== */

export async function extractFileContent(file) {
  const ext = file.name.split('.').pop().toLowerCase();

  if (ext === 'pdf') {
    const url = URL.createObjectURL(file);
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        // Preserve line breaks: insert \n when a text item's Y coordinate
        // shifts more than ~2px from the previous one. Without this, pdfjs
        // collapses everything into one long line and section detection fails.
        let pageText = '';
        let lastY = null;
        for (const item of content.items) {
          if (!item.str) continue;
          const currentY = item.transform ? item.transform[5] : null;
          if (lastY !== null && currentY !== null && Math.abs(currentY - lastY) > 2) {
            pageText += '\n';
          } else if (pageText && !/\s$/.test(pageText)) {
            pageText += ' ';
          }
          pageText += item.str;
          lastY = currentY;
        }
        text += pageText + '\n';
      }
      return text;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  if (ext === 'docx') {
    const buf = await file.arrayBuffer();
    const { value } = await mammoth.extractRawText({ arrayBuffer: buf });
    return value;
  }

  if (ext === 'txt') {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
      reader.readAsText(file);
    });
  }

  throw new Error('Only PDF, DOCX, TXT are supported.');
}

/* ============================================================
 * Heading dictionary
 * ========================================================== */

// Each entry maps a normalized field key → array of heading aliases.
// Headings are matched case-insensitively, with optional trailing :, -, –, —.
const HEADING_MAP = {
  jobTitle: [
    'job title', 'title', 'role', 'position', 'designation',
    'role title', 'job role', 'position title', 'hiring for',
    'opening for', 'role name', 'job profile', 'we are hiring',
    'we\'re hiring', 'now hiring', 'hiring',
  ],
  jobType: [
    'job type', 'employment type', 'position type', 'type of employment',
    'employment status', 'work type', 'engagement type', 'contract type',
    'work arrangement', 'nature of work', 'role type',
  ],
  workModel: [
    'work mode', 'work model', 'work location type', 'mode of work',
    'workplace type', 'remote/onsite', 'remote / onsite', 'mode',
    'work setup', 'workplace', 'work environment', 'remote / hybrid / onsite',
  ],
  location: [
    'location', 'job location', 'work location', 'based in', 'office location',
    'place of work', 'city', 'locations', 'work from', 'office',
    'cities', 'geo', 'geography', 'work city', 'work locations',
  ],
  experience: [
    'experience', 'experience required', 'required experience', 'years of experience',
    'work experience', 'minimum experience', 'exp', 'years',
    'min experience', 'min exp', 'years of exp', 'experience needed',
    'total experience', 'overall experience', 'industry experience',
  ],
  primarySkills: [
    'primary skills', 'must have skills', 'must-have skills', 'must have',
    'required skills', 'mandatory skills', 'core skills', 'technical skills',
    'key skills', 'keyskills', 'skills required', 'skills', 'tech stack',
    'technology stack', 'technologies', 'keyskills - must have',
    'required skills & qualifications', 'required skills and qualifications',
    'must-have qualifications', 'qualifications and skills',
    'required experience and skills', 'skills & qualifications',
    'required tech', 'tech requirements', 'technical requirements',
    'core competencies', 'core requirements', 'must have technical skills',
    'required competencies', 'tools & technologies',
  ],
  secondarySkills: [
    'secondary skills', 'good to have', 'good-to-have', 'nice to have',
    'nice-to-have', 'preferred skills', 'optional skills', 'desired skills',
    'additional skills', 'keyskills - nice to have',
    'expected and desired skills',
    'preferred / nice-to-have skills', 'preferred / nice to have skills',
    'preferred nice-to-have skills', 'preferred or nice-to-have skills',
    'bonus skills', 'plus points', 'bonus points', 'plus', 'pluses',
    'extras', 'value add', 'value-add', 'bonus', 'preferred qualifications',
  ],
  responsibilities: [
    'responsibilities', 'job responsibilities', 'key responsibilities',
    'roles and responsibilities', 'duties', 'key duties',
    'what you will do', 'what you\'ll do', 'your responsibilities',
    'role description', 'role responsibilities',
    'what you\'ll be doing', 'what you will be doing',
    'in this role you will', 'day-to-day responsibilities',
    'day to day responsibilities', 'your role', 'role highlights',
    'job duties', 'tasks', 'primary responsibilities', 'main responsibilities',
  ],
  jobDescription: [
    'job description', 'job desc', 'about the role', 'about the job',
    'role overview', 'job summary', 'project summary', 'summary',
    'overview', 'description', 'position overview', 'position summary',
    'role summary', 'about this role', 'about this opportunity', 'opportunity',
  ],
  requirements: [
    'requirements', 'requirement', 'qualifications', 'qualification',
    'eligibility', 'eligibility criteria', 'who you are', 'what we expect',
    'required qualifications', 'eligibility requirements',
    'who should apply', 'ideal candidate', 'we want you if',
    'candidate profile', 'desired profile', 'expected qualifications',
  ],
  education: [
    'education', 'educational qualifications', 'academic qualifications',
    'academic background', 'degree', 'qualification',
    'academic requirements', 'minimum education', 'educational background',
    'educational requirements',
  ],
  certifications: [
    'certifications', 'certification', 'certificates', 'preferred certifications',
    'required certifications', 'professional certifications',
  ],
  noticePeriod: [
    'notice period', 'joining time', 'availability', 'when to start',
    'start date', 'joining', 'expected joining',
  ],
  department: [
    'department', 'team', 'function', 'business unit', 'practice',
    'group', 'vertical', 'division',
  ],
  industry: [
    'industry', 'domain', 'sector', 'industry type', 'business domain',
  ],
  companyName: [
    'company', 'company name', 'about us', 'about the company', 'organization',
    'employer', 'who we are', 'meet us', 'our company',
  ],
  numberOfOpenings: [
    'number of openings', 'no. of openings', 'no of openings', 'openings',
    'positions', 'vacancies', 'no. of positions', 'no of positions',
    'number of positions', 'vacancies available', 'open positions',
  ],
  externalSystemID: [
    'job id', 'job code', 'req id', 'requisition id', 'requisition number',
    'reference id', 'reference number', 'reference', 'ref id', 'ref no',
    'external id', 'job reference', 'job ref', 'requisition #',
    'position id', 'posting id',
  ],
  priority: ['priority', 'urgency', 'priority level'],
  payBillRate: [
    'salary', 'compensation', 'pay', 'pay rate', 'bill rate', 'pay/bill rate',
    'pay & bill rate', 'pay and bill rate', 'package', 'ctc', 'remuneration',
    'salary range', 'hourly rate', 'annual salary', 'salary package',
    'compensation package', 'expected ctc', 'budget',
  ],
  benefits: [
    'benefits', 'perks', 'perks and benefits', 'what we offer',
    'what you\'ll get', 'what you will get', 'why join us',
    'we offer', 'employee benefits', 'compensation & benefits',
    'compensation and benefits', 'benefits & perks',
  ],
  shiftTiming: [
    'shift', 'shift timing', 'shift timings', 'working hours', 'work hours',
    'timing', 'timings', 'hours', 'shift type', 'time zone',
    'work schedule', 'schedule',
  ],
  travelRequired: [
    'travel', 'travel required', 'travel requirement', 'travel %',
    'percentage travel', 'travel percentage',
  ],
  languagesRequired: [
    'languages', 'language requirements', 'languages required',
    'language proficiency', 'language skills',
  ],
  reportingTo: [
    'reports to', 'reporting to', 'reporting manager', 'manager',
    'will report to', 'direct manager', 'supervisor',
  ],
  teamSize: [
    'team size', 'size of team', 'reports', 'direct reports', 'reportees',
  ],
  gender: ['gender', 'gender preference'],
};

// Build flat lookup: lowercased heading → field key
const HEADING_LOOKUP = (() => {
  const map = new Map();
  for (const [key, aliases] of Object.entries(HEADING_MAP)) {
    aliases.forEach((alias) => map.set(alias.toLowerCase(), key));
  }
  return map;
})();

/* ============================================================
 * Section parser — splits JD text into a sections map
 * ========================================================== */

/** Strip leading bullet markers, numbered list prefix ("1." / "1)"),
 *  and trailing punctuation from a candidate heading line.
 *  Important: do NOT strip arbitrary digits — that would turn
 *  "• 6-8 years of experience" into "years of experience" and falsely
 *  match the `experience` heading alias. */
function cleanHeadingCandidate(line) {
  return line
    .replace(/^[\s•*]+/, '') // bullet / star markers
    .replace(/^[-–—]\s+/, '') // single leading dash + space (not inside ranges)
    .replace(/^(\d+[.)])\s+/, '') // numbered list prefix
    .replace(/[:\-–—\s]+$/, '')
    .trim();
}

/** Detect if a line is a section heading. Returns the canonical field key or null. */
function detectHeading(line) {
  if (!line || line.length > 80) return null;

  // Inline form "Heading: value" or "Heading - value"
  const inlineMatch = line.match(/^([^:\-–—]{1,60})\s*[:\-–—]\s*(.+)$/);
  if (inlineMatch) {
    const candidate = cleanHeadingCandidate(inlineMatch[1]).toLowerCase();
    if (HEADING_LOOKUP.has(candidate)) {
      return { key: HEADING_LOOKUP.get(candidate), inlineValue: inlineMatch[2].trim() };
    }
  }

  // Pure heading line "Heading" or "Heading:"
  const cleaned = cleanHeadingCandidate(line).toLowerCase();
  if (HEADING_LOOKUP.has(cleaned)) {
    return { key: HEADING_LOOKUP.get(cleaned), inlineValue: null };
  }

  return null;
}

/** Quick heuristic: does this line look like a job title? */
function looksLikeTitle(line) {
  if (!line) return false;
  const trimmed = line.trim();
  if (trimmed.length < 4 || trimmed.length > 100) return false;
  if (/[.!?]$/.test(trimmed)) return false;
  if (/^(the|we|this|our|you|please|are|is|in)\b/i.test(trimmed)) return false;
  if (detectHeading(trimmed)) return false;
  // Reject heading-like phrases that slip past HEADING_MAP. Catches
  // "Role Overview", "Key Responsibilities", "Required Skills", etc.
  if (
    /^(role|position|job|key|required|preferred|nice|about|overview|summary|responsibilities|skills|qualifications|requirements)\s*(overview|summary|details|description|responsibilities|skills|qualifications|requirements|to[\s-]have)?\s*$/i.test(
      trimmed,
    )
  ) {
    return false;
  }
  // Must contain at least one capital letter
  if (!/[A-Z]/.test(trimmed)) return false;
  return true;
}

/** Group raw text into a sections object keyed by canonical field name. */
function extractSections(text) {
  const lines = text
    .replace(/\r/g, '')
    .split('\n')
    .map((l) => l.trim());

  const sections = {};
  let currentKey = null;
  let buffer = [];

  const flush = () => {
    if (currentKey && buffer.length > 0) {
      // Preserve line boundaries — downstream skill/responsibilities
      // extractors rely on \n to detect implicit list items in JDs that
      // omit bullet markers.
      const value = buffer.join('\n').replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim();
      if (value) {
        sections[currentKey] = sections[currentKey]
          ? sections[currentKey] + '\n' + value
          : value;
      }
    }
    buffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;

    const detected = detectHeading(line);
    if (detected) {
      flush();

      // Special handling for jobTitle / jobDescription headings:
      // when value is on the next line and looks like a title, capture as jobTitle.
      if (detected.key === 'jobTitle' && !detected.inlineValue) {
        const next = lines[i + 1];
        if (looksLikeTitle(next)) {
          sections.jobTitle = next.trim();
          i++;
          currentKey = null;
          continue;
        }
      }
      if (detected.key === 'jobDescription' && !sections.jobTitle) {
        // "Job Description:\n<Title>" pattern — value on next line is likely title
        if (!detected.inlineValue) {
          const next = lines[i + 1];
          if (looksLikeTitle(next)) {
            sections.jobTitle = next.trim();
            i++;
            currentKey = 'jobDescription';
            continue;
          }
        } else if (looksLikeTitle(detected.inlineValue)) {
          // "Job Description: Front End Developer" inline form — value is the title
          sections.jobTitle = detected.inlineValue.trim();
          currentKey = null;
          continue;
        }
      }

      currentKey = detected.key;
      if (detected.inlineValue) {
        buffer.push(detected.inlineValue);
      }
      continue;
    }

    if (currentKey) {
      buffer.push(line);
    }
  }

  flush();

  // If no explicit responsibilities heading was found, derive from bullet
  // lists that landed in jobDescription. Many JDs (Climate Action Reserve,
  // Keka samples) use bold subsections + bullets without an explicit
  // "Responsibilities" heading.
  if (!sections.responsibilities && sections.jobDescription) {
    const bullets = extractBulletItems(sections.jobDescription);
    if (bullets.length >= 3) {
      sections.responsibilities = bullets.join('. ');
    }
  }

  return sections;
}

/** Pull bullet-point items out of a flat string. Bullet markers: • * - – — o */
function extractBulletItems(text) {
  if (!text) return [];
  // Require at least 3 bullet markers in the text before treating as a list,
  // to avoid false positives on dash-separated phrases.
  const markerCount = (text.match(/[•*]/g) || []).length;
  if (markerCount < 3) return [];
  const re = /[•*]\s*([^•*\n]+?)(?=(?:\s*[•*])|$)/g;
  const items = [];
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(text)) !== null) {
    let item = m[1].trim().replace(/^[•*\-–—\s]+/, '').replace(/[.;]+$/, '');
    if (item.length < 4) continue;
    if (item.length > 200) item = item.slice(0, 200).trim();
    items.push(item);
  }
  return items;
}

/* ============================================================
 * Field-level extractors (regex + heuristics)
 * ========================================================== */

const VALID_WORK_MODELS = ['Remote', 'On-site', 'Hybrid'];
const VALID_JOB_TYPES = ['Full time', 'Part time', 'Intern', 'Contract'];
const VALID_PRIORITIES = ['High', 'Medium', 'Low'];
const VALID_COMPENSATION_TYPES = ['Hourly', 'Annual', 'Monthly'];

// Common skills dictionary — case-insensitive match against text body.
// Order roughly by category for readable output.
const SKILL_DICTIONARY = [
  // Languages
  'JavaScript', 'TypeScript', 'Python', 'Java', 'Kotlin', 'Swift', 'Go', 'Golang',
  'Rust', 'C++', 'C#', '.NET', 'Ruby', 'PHP', 'Scala', 'Perl', 'R', 'Dart', 'Elixir',
  // Frontend
  'React', 'React.js', 'ReactJS', 'Next.js', 'Vue', 'Vue.js', 'Angular', 'AngularJS',
  'Svelte', 'jQuery', 'Redux', 'MobX', 'Tailwind', 'Tailwind CSS', 'Bootstrap',
  'Material UI', 'MUI', 'HTML5', 'CSS3', 'HTML', 'CSS', 'SASS', 'SCSS', 'Webpack', 'Vite',
  // Backend
  'Node.js', 'NodeJS', 'Express', 'Express.js', 'NestJS', 'Nest.js', 'Spring Boot',
  'Spring', 'Django', 'Flask', 'FastAPI', 'Laravel', 'Rails', 'ASP.NET',
  // Mobile
  'React Native', 'Flutter', 'iOS', 'Android', 'SwiftUI', 'Jetpack Compose',
  // Databases
  'MySQL', 'PostgreSQL', 'Postgres', 'MongoDB', 'Redis', 'Elasticsearch',
  'DynamoDB', 'Cassandra', 'SQLite', 'Oracle', 'SQL Server', 'MSSQL', 'Snowflake',
  'BigQuery', 'Firebase', 'Firestore', 'GraphQL',
  // Cloud / DevOps
  'AWS', 'Azure', 'GCP', 'Google Cloud', 'Docker', 'Kubernetes', 'K8s',
  'Terraform', 'Ansible', 'Jenkins', 'CircleCI', 'GitHub Actions', 'GitLab CI',
  'Helm', 'ArgoCD', 'Prometheus', 'Grafana', 'Datadog', 'New Relic', 'Splunk',
  'CloudFormation', 'Lambda', 'EC2', 'S3', 'RDS', 'EKS', 'ECS',
  // Data / ML
  'TensorFlow', 'PyTorch', 'Keras', 'scikit-learn', 'Pandas', 'NumPy',
  'Spark', 'Hadoop', 'Kafka', 'Airflow', 'dbt', 'Tableau', 'Power BI', 'Looker',
  // Testing
  'Jest', 'Mocha', 'Cypress', 'Playwright', 'Selenium', 'JUnit', 'TestNG', 'PyTest',
  // Other
  'Git', 'GitHub', 'GitLab', 'Bitbucket', 'JIRA', 'Confluence', 'Linux', 'Bash',
  'REST', 'REST API', 'gRPC', 'WebSocket', 'OAuth', 'JWT', 'SAML', 'Microservices',
  'CI/CD', 'TDD', 'Agile', 'Scrum', 'Kanban',
  // CMS / web platforms
  'WordPress', 'Drupal', 'Joomla', 'Shopify', 'Magento', 'Webflow', 'Wix',
  'CakePHP', 'CodeIgniter', 'Symfony', 'Contentful', 'Strapi', 'HubSpot',
  // Design tools
  'Figma', 'Sketch', 'Adobe XD', 'Photoshop', 'Illustrator', 'InDesign',
  'Adobe Creative Suite', 'Dreamweaver', 'Fireworks', 'After Effects',
  // Office / productivity
  'Microsoft Office', 'Excel', 'PowerPoint', 'Word', 'Google Workspace',
  'Salesforce', 'SAP', 'ServiceNow', 'Slack', 'Notion',
  // Networking / OS
  'Windows', 'macOS', 'Ubuntu', 'CentOS', 'Networking', 'TCP/IP', 'DNS', 'VPN',
  // QA / Testing tools
  'Pytest', 'RestAssured', 'Rest Assured', 'Postman', 'TestRail',
  'JMeter', 'Gatling', 'BDD', 'TDD', 'ETL', 'Data Pipeline',
  'SoapUI', 'Karate', 'Appium', 'Robot Framework', 'Cucumber',
  // .NET ecosystem extras
  '.NET Core', '.NET 6', '.NET 7', '.NET 8', 'ASP.NET', 'ASP.NET Core',
  'Entity Framework', 'LINQ', 'NUnit', 'xUnit',
  // Graph databases
  'Neo4j', 'Amazon Neptune', 'Neptune', 'ArangoDB', 'OrientDB',
  'Cypher', 'Gremlin',
  // AWS services
  'CloudWatch', 'API Gateway', 'SNS', 'SQS', 'Step Functions',
  'CloudFront', 'Route 53',
  // Domain / industry
  'Capital Markets', 'Fintech', 'Financial Services',
  'Recommendation Engine', 'Fraud Detection',
  // More languages
  'Objective-C', 'Haskell', 'Lua', 'Solidity', 'MATLAB', 'COBOL',
  'Fortran', 'Assembly', 'F#', 'Clojure', 'Erlang', 'Groovy', 'Crystal',
  // More backend frameworks
  'Phoenix', 'Sinatra', 'Gin', 'Echo', 'Fiber', 'Actix', 'Rocket', 'Slim',
  'Yii', 'Zend', 'Phalcon', 'Bottle', 'Tornado', 'Pyramid', 'Sanic',
  'Hapi', 'Koa', 'Fastify', 'Micronaut', 'Quarkus', 'Vert.x',
  // Frontend ecosystem
  'Recoil', 'Zustand', 'React Query', 'TanStack', 'Shadcn', 'Chakra UI',
  'Ant Design', 'PrimeReact', 'Storybook', 'Emotion', 'Styled Components',
  'Framer Motion', 'NgRx', 'Pinia', 'Vuex', 'Nuxt', 'Nuxt.js', 'Remix',
  'Gatsby', 'Astro', 'Qwik', 'SolidJS', 'Solid.js',
  // Build / bundlers
  'ESBuild', 'Rollup', 'Parcel', 'Turbopack', 'Bun', 'SWC', 'Babel',
  // More testing tools
  'Vitest', 'React Testing Library', 'Enzyme', 'WebdriverIO', 'Detox',
  'K6', 'Locust', 'Playwright Test', 'Codeception',
  // More DBs
  'ClickHouse', 'CockroachDB', 'ScyllaDB', 'Couchbase', 'RethinkDB',
  'InfluxDB', 'TimescaleDB', 'MariaDB', 'Vertica', 'Teradata', 'Greenplum',
  'Neo4J', 'ArangoDB', 'JanusGraph',
  // DevOps / infra extras
  'Vagrant', 'Chef', 'Puppet', 'SaltStack', 'Flux', 'Spinnaker',
  'Rancher', 'OpenShift', 'Octopus Deploy', 'TeamCity', 'Bamboo',
  'Travis CI', 'Drone CI', 'Buildkite', 'AWS CodePipeline',
  'Pulumi', 'CDK', 'Packer', 'Vault',
  // Monitoring / logging / observability
  'Sentry', 'Logstash', 'Fluentd', 'OpenTelemetry', 'Jaeger', 'Zipkin',
  'Loki', 'AlertManager', 'PagerDuty', 'AppDynamics', 'Dynatrace',
  'Honeycomb', 'Lightstep',
  // Messaging / streaming
  'RabbitMQ', 'ActiveMQ', 'NATS', 'ZeroMQ', 'MQTT', 'EventBridge',
  'Kinesis', 'Pub/Sub', 'Amazon MQ',
  // Big data / streaming
  'Hive', 'Pig', 'HBase', 'Presto', 'Trino', 'Druid', 'Apache Beam',
  'Storm', 'Flink', 'Databricks', 'Delta Lake',
  // Data viz / BI
  'D3', 'D3.js', 'Plotly', 'Chart.js', 'Highcharts', 'ECharts',
  'Streamlit', 'Apache Superset', 'Metabase', 'Mode Analytics',
  'Qlik', 'QlikView', 'QlikSense', 'Sisense',
  // ML / AI
  'OpenAI', 'Anthropic', 'LangChain', 'LlamaIndex', 'Hugging Face',
  'MLflow', 'Kubeflow', 'ONNX', 'OpenCV', 'YOLO', 'BERT', 'GPT',
  'Transformer', 'LLM', 'Generative AI', 'GenAI', 'RAG',
  'Stable Diffusion', 'Whisper', 'Claude', 'Gemini', 'Llama',
  'Vector Database', 'Pinecone', 'Weaviate', 'Milvus', 'Qdrant', 'Chroma',
  // Mobile cross-platform
  'Xamarin', 'Cordova', 'Ionic', 'NativeScript', 'Capacitor', 'Expo',
  'KMP', 'Kotlin Multiplatform',
  // Embedded / IoT
  'Embedded C', 'RTOS', 'FreeRTOS', 'Arduino', 'Raspberry Pi',
  'Zephyr', 'PLC', 'CAN bus',
  // Security
  'Burp Suite', 'Metasploit', 'Nessus', 'Wireshark', 'Nmap', 'OWASP',
  'Snyk', 'SonarQube', 'Veracode', 'HashiCorp Vault', 'Auth0', 'Okta',
  'Keycloak', 'Cognito', 'IAM', 'SSO', 'MFA', 'Zero Trust',
  'PenTest', 'SAST', 'DAST',
  // API / architecture
  'tRPC', 'AsyncAPI', 'Swagger', 'OpenAPI', 'Insomnia', 'Apigee',
  'Kong', 'WSO2', 'Service Mesh', 'Istio', 'Linkerd', 'Envoy',
  // CRM / ERP / iPaaS
  'Dynamics 365', 'NetSuite', 'Oracle Fusion', 'Workday', 'PeopleSoft',
  'Apache Camel', 'MuleSoft', 'Talend', 'Informatica', 'Boomi',
  'Tibco', 'IBM DataStage', 'SSIS', 'SSRS', 'SSAS',
  // Cloud (additional)
  'CloudFront', 'Route 53', 'Athena', 'Redshift', 'EMR', 'Glue',
  'SageMaker', 'AppSync', 'Amplify', 'Azure Functions', 'Cosmos DB',
  'Service Bus', 'Event Hub', 'App Service', 'AKS', 'ACR',
  'Cloud Run', 'Cloud Functions', 'Cloud Storage', 'Dataflow',
  'Dataproc', 'Vertex AI', 'Firestore',
  // Process / methodology
  'Lean', 'Waterfall', 'XP', 'SAFe', 'DevSecOps', 'GitOps', 'Site Reliability',
  'SRE', 'Platform Engineering',
];

// Build dictionary regex once (escaped, word-boundary tolerant for symbols)
const SKILL_REGEX = (() => {
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = SKILL_DICTIONARY
    .map((s) => escape(s))
    .sort((a, b) => b.length - a.length) // prefer longer matches first
    .join('|');
  return new RegExp(`(?<![A-Za-z0-9])(${pattern})(?![A-Za-z0-9])`, 'gi');
})();

function extractSkillsFromText(text) {
  if (!text) return [];
  const seen = new Map(); // canonical (lower) → original casing
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = SKILL_REGEX.exec(text)) !== null) {
    const raw = m[1];
    // canonicalize against dictionary (preserve original casing)
    const canonical = SKILL_DICTIONARY.find(
      (s) => s.toLowerCase() === raw.toLowerCase(),
    ) || raw;
    if (!seen.has(canonical.toLowerCase())) {
      seen.set(canonical.toLowerCase(), canonical);
    }
  }
  return Array.from(seen.values());
}

function extractWorkModel(text) {
  const t = text.toLowerCase();
  if (/\b(hybrid(?:\s*model)?|flex\s*work|flexible\s*work)\b/.test(t)) return 'Hybrid';
  if (/\b(100%\s*remote|fully\s*remote|wfh|work\s*from\s*home|remote\s*work|remote\s*role|remote\s*position|remote)\b/.test(t)) {
    return 'Remote';
  }
  if (/\b(on[\s-]?site|onsite|in[\s-]?office|in\s*office|on\s*premise|on-premise|wfo|work\s*from\s*office)\b/.test(t)) {
    return 'On-site';
  }
  return '';
}

function extractJobType(text) {
  const t = text.toLowerCase();
  const types = new Set();
  if (/\b(full[\s-]?time|fulltime|permanent\s*role|permanent\s*position|perm\s*role)\b/.test(t)) {
    types.add('Full time');
  }
  if (/\b(part[\s-]?time|parttime)\b/.test(t)) types.add('Part time');
  if (
    /\b(contract(?:or|[\s-]?to[\s-]?hire|\s*role|\s*position|\s*basis)?|c2h|freelance|freelancer|temporary|consulting\s*role)\b/.test(
      t,
    )
  ) {
    types.add('Contract');
  }
  if (/\b(intern(?:ship)?|trainee|graduate\s*program)\b/.test(t)) types.add('Intern');
  return Array.from(types).join(',');
}

function extractExperience(text) {
  // Range patterns: "5+ years", "5-10 years", "5 to 10 years", "5 - 8 yrs"
  const range = text.match(
    /(\d+)\s*(?:\+|-|to|–|—)\s*(\d+)?\s*(?:\+)?\s*(?:years?|yrs?|y)\b/i,
  );
  if (range) {
    const min = range[1];
    const max = range[2] || '';
    const display = max ? `${min}-${max} years` : `${min}+ years`;
    return { experience: display, minExperience: min, maxExperience: max };
  }
  // "Minimum 5 years" / "At least 5 years" / "Min 5 yrs"
  const min = text.match(
    /\b(?:minimum|at\s*least|min(?:imum)?|atleast)\s*(?:of\s*)?(\d+)\s*\+?\s*(?:years?|yrs?|y)\b/i,
  );
  if (min) {
    return { experience: `${min[1]}+ years`, minExperience: min[1], maxExperience: '' };
  }
  // Plain "5+ years" / "5 years experience"
  const single = text.match(/(\d+)\s*\+?\s*(?:years?|yrs?|y)\b/i);
  if (single) {
    const n = single[1];
    return { experience: `${n}+ years`, minExperience: n, maxExperience: '' };
  }
  return { experience: '', minExperience: '', maxExperience: '' };
}

function extractCompensation(text) {
  // Look for common compensation patterns. Returns first plausible match.
  const out = {
    payBillRate: '',
    compensationMin: '',
    compensationMax: '',
    compensationType: '',
    currency: '',
  };

  // 1) Indian LPA / lakhs
  const lpa = text.match(
    /(?:₹|rs\.?|inr)?\s*(\d+(?:\.\d+)?)\s*(?:-|to|–|—)\s*(\d+(?:\.\d+)?)\s*(?:lpa|lakhs?\s*(?:per\s*annum)?|l\s*p\s*a)/i,
  );
  if (lpa) {
    out.payBillRate = `${lpa[1]}-${lpa[2]} LPA`;
    out.compensationMin = lpa[1];
    out.compensationMax = lpa[2];
    out.compensationType = 'Annual';
    out.currency = 'INR';
    return out;
  }
  const lpaSingle = text.match(
    /(?:₹|rs\.?|inr)?\s*(\d+(?:\.\d+)?)\s*(?:lpa|lakhs?\s*(?:per\s*annum)?|l\s*p\s*a)/i,
  );
  if (lpaSingle) {
    out.payBillRate = `${lpaSingle[1]} LPA`;
    out.compensationMin = lpaSingle[1];
    out.compensationType = 'Annual';
    out.currency = 'INR';
    return out;
  }

  // 2) Hourly: $X/hr or $X-$Y per hour
  const hourlyRange = text.match(
    /([$₹£€])\s?(\d+(?:\.\d+)?)\s*(?:-|to|–|—)\s*\1?\s?(\d+(?:\.\d+)?)\s*\/?\s*(?:per\s*hour|hour|hr|hourly)/i,
  );
  if (hourlyRange) {
    out.payBillRate = `${hourlyRange[1]}${hourlyRange[2]}-${hourlyRange[1]}${hourlyRange[3]}/hr`;
    out.compensationMin = hourlyRange[2];
    out.compensationMax = hourlyRange[3];
    out.compensationType = 'Hourly';
    out.currency = symbolToCurrency(hourlyRange[1]);
    return out;
  }
  const hourlySingle = text.match(
    /([$₹£€])\s?(\d+(?:\.\d+)?)\s*\/?\s*(?:per\s*hour|hour|hr|hourly)/i,
  );
  if (hourlySingle) {
    out.payBillRate = `${hourlySingle[1]}${hourlySingle[2]}/hr`;
    out.compensationMin = hourlySingle[2];
    out.compensationType = 'Hourly';
    out.currency = symbolToCurrency(hourlySingle[1]);
    return out;
  }

  // 3) Annual range with currency: $80,000-$120,000 / year, €60k-€80k
  const annualRange = text.match(
    /([$₹£€])\s?([\d,]+(?:\.\d+)?)\s*k?\s*(?:-|to|–|—)\s*\1?\s?([\d,]+(?:\.\d+)?)\s*k?\s*(?:per\s*(?:year|annum)|annually|annual|p\.?a\.?|\/yr|\/year)?/i,
  );
  if (annualRange) {
    const minNum = parseAmount(annualRange[2], text.includes('k'));
    const maxNum = parseAmount(annualRange[3], text.includes('k'));
    out.payBillRate = `${annualRange[1]}${annualRange[2]} - ${annualRange[1]}${annualRange[3]}`;
    out.compensationMin = String(minNum);
    out.compensationMax = String(maxNum);
    out.compensationType = /per\s*(?:year|annum)|annually|annual|p\.?a\.?|\/yr|\/year/i.test(text)
      ? 'Annual'
      : '';
    out.currency = symbolToCurrency(annualRange[1]);
    return out;
  }

  // 4) Plain single amount with currency
  const single = text.match(
    /([$₹£€])\s?([\d,]+(?:\.\d+)?)\s*(?:per\s*(year|annum|month|hour)|annually|monthly|hourly|p\.?a\.?|\/yr|\/year|\/mo|\/month|\/hr|\/hour)?/i,
  );
  if (single) {
    out.payBillRate = `${single[1]}${single[2]}`;
    out.compensationMin = String(parseAmount(single[2], false));
    out.currency = symbolToCurrency(single[1]);
    const tail = (single[3] || '').toLowerCase();
    if (/year|annum/.test(tail) || /annual|p\.?a\.?|\/yr|\/year/i.test(text)) {
      out.compensationType = 'Annual';
    } else if (/month/.test(tail) || /monthly|\/mo|\/month/i.test(text)) {
      out.compensationType = 'Monthly';
    } else if (/hour/.test(tail) || /hourly|\/hr|\/hour/i.test(text)) {
      out.compensationType = 'Hourly';
    }
    return out;
  }

  return out;
}

function parseAmount(str, isK) {
  const n = parseFloat(str.replace(/,/g, ''));
  return isK ? n * 1000 : n;
}

function symbolToCurrency(sym) {
  switch (sym) {
    case '₹':
      return 'INR';
    case '£':
      return 'GBP';
    case '€':
      return 'EUR';
    case '$':
      return ''; // ambiguous
    default:
      return '';
  }
}

function extractCurrencyCode(text) {
  const m = text.match(/\b(USD|CAD|INR|EUR|GBP|AUD|SGD|AED|JPY|CNY|CHF|NZD)\b/i);
  if (m) return m[1].toUpperCase();
  // Infer from country mention when ambiguous $ is used.
  const hasDollar = /\$/.test(text);
  if (hasDollar) {
    if (/\b(?:USA|United\s*States|U\.S\.A?\.?|US)\b/.test(text)) return 'USD';
    if (/\b(?:Canada|Canadian)\b/i.test(text)) return 'CAD';
    if (/\b(?:Australia|Australian)\b/i.test(text)) return 'AUD';
    if (/\b(?:Singapore|Singaporean)\b/i.test(text)) return 'SGD';
  }
  if (/\b(?:rupees?|Rs\.?)\b/i.test(text)) return 'INR';
  if (/\b(?:euros?)\b/i.test(text)) return 'EUR';
  if (/\b(?:pounds?\s*(?:sterling)?)\b/i.test(text)) return 'GBP';
  return '';
}

function extractNoticePeriod(text) {
  if (/\b(?:immediate\s*joiners?|immediate\s*joining|ready\s*to\s*join|joining\s*asap|asap)\b/i.test(text)) {
    return 'Immediate';
  }
  if (/\b(?:0\s*[-to]\s*15\s*days|15\s*days\s*notice|0-15\s*days)\b/i.test(text)) {
    return '0-15 days';
  }
  if (/\b(?:serving\s*notice)\b/i.test(text)) return 'Serving notice';
  const m = text.match(/(\d+)\s*(days?|weeks?|months?)\s*(?:notice|notice\s*period)?/i);
  if (m) return `${m[1]} ${m[2]}`;
  return '';
}

function extractEducation(text) {
  const degrees = [
    'Ph\\.?D', 'Doctorate',
    'M\\.?Tech', 'M\\.?S\\.?', 'M\\.?Sc', 'MBA', 'MCA', 'M\\.?E', 'M\\.?B\\.?A',
    'B\\.?Tech', 'B\\.?S\\.?', 'B\\.?Sc', 'BCA', 'B\\.?E', 'B\\.?Com', 'B\\.?A',
    'Bachelor[\'s]*\\s*(?:degree|in|of)?\\s*[A-Za-z\\s]*',
    'Master[\'s]*\\s*(?:degree|in|of)?\\s*[A-Za-z\\s]*',
    'Diploma', 'PG\\s*Diploma', 'Post\\s*Graduate', 'Postgraduate',
    'Post\\s*Graduation', 'Graduation', 'Undergraduate', 'Engineering\\s*degree',
  ];
  const re = new RegExp(`\\b(${degrees.join('|')})[A-Za-z\\.\\s]*`, 'i');
  const m = text.match(re);
  return m ? m[0].replace(/\s+/g, ' ').trim() : '';
}

function extractCertifications(text) {
  const certs = [
    'AWS Certified [A-Za-z\\s]*',
    'AWS Solutions Architect[A-Za-z\\s-]*',
    'AWS DevOps[A-Za-z\\s-]*',
    'Azure Certified [A-Za-z\\s]*',
    'Azure Administrator[A-Za-z\\s-]*',
    'GCP Certified [A-Za-z\\s]*',
    'GCP Professional[A-Za-z\\s-]*',
    'PMP', 'PMI-ACP', 'CSM', 'PSM', 'PSPO', 'CISSP', 'CCNA', 'CCNP', 'CCIE',
    'OCJP', 'OCA', 'OCP', 'OCM', 'ITIL', 'ITIL v4',
    'Six Sigma', 'Six Sigma Black Belt', 'Six Sigma Green Belt',
    'CPA', 'CFA', 'CMA', 'PRINCE2', 'TOGAF', 'SAFe', 'SAFe Agilist',
    'Scrum Master Certified', 'CKA', 'CKAD', 'CKS',
    'RHCE', 'RHCSA', 'MCSA', 'MCSE', 'MCSD',
    'CISA', 'CISM', 'CEH', 'OSCP', 'CompTIA[A-Za-z\\s+]*',
    'JLPT', 'IELTS', 'TOEFL', 'GMAT', 'GRE',
  ];
  const re = new RegExp(`\\b(${certs.join('|')})\\b`, 'gi');
  const found = new Set();
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(text)) !== null) {
    found.add(m[1].trim());
  }
  return Array.from(found).join(', ');
}

function extractNumberOfOpenings(text) {
  const m = text.match(/(?:number\s*of\s*openings?|openings?|positions?|vacancies?)\s*[:-]?\s*(\d+)/i);
  if (m) return m[1];
  const inline = text.match(/(\d+)\s+(?:openings?|positions?|vacancies?)\b/i);
  return inline ? inline[1] : '';
}

function extractExternalSystemID(text) {
  // Require an explicit separator after the label (`:` `-` `#`) and ensure
  // the captured ID contains at least one digit or hyphen — pure words like
  // "uirements" (the tail of "Requirements") are rejected.
  const labeled = text.match(
    /(?:job\s*id|job\s*code|requisition\s*(?:id|number|#)?|reference(?:\s*(?:id|number|#))?|ref(?:\s*(?:id|no|#)))\s*[:\-#]\s*([A-Za-z0-9][A-Za-z0-9\-_/]{2,20})/i,
  );
  if (labeled) {
    const id = labeled[1].trim();
    // Reject pure alphabetic captures (must contain a digit or separator)
    if (/[\d\-_/]/.test(id)) return id;
  }
  return '';
}

function extractPriority(text) {
  const m = text.match(/priority\s*[:-]?\s*(high|medium|low)/i);
  if (m) {
    const v = m[1].toLowerCase();
    return v.charAt(0).toUpperCase() + v.slice(1);
  }
  return '';
}

function extractGender(text) {
  const m = text.match(/gender\s*(?:preference)?\s*[:-]?\s*(male|female|other|any)/i);
  if (!m) return '';
  const v = m[1].toLowerCase();
  if (v === 'any') return '';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function extractTeamSize(text) {
  const m = text.match(/team\s*size\s*[:-]?\s*(\d+(?:\s*[-to]+\s*\d+)?)/i);
  return m ? m[1].trim() : '';
}

function extractTravelRequired(text) {
  const m = text.match(/travel\s*(?:required|requirement)?\s*[:-]?\s*([^\n.]{1,80})/i);
  return m ? m[1].trim() : '';
}

function extractShiftTiming(text) {
  const m = text.match(
    /(?:shift|working\s*hours|timings?)\s*[:-]?\s*([^\n.]{1,80})/i,
  );
  return m ? m[1].trim() : '';
}

function extractReportingTo(text) {
  const m = text.match(/(?:reports?\s*to|reporting\s*to)\s*[:-]?\s*([^\n.]{1,80})/i);
  return m ? m[1].trim() : '';
}

function extractLanguagesRequired(text) {
  const langs = [
    'English', 'Hindi', 'Telugu', 'Tamil', 'Kannada', 'Malayalam', 'Marathi',
    'Bengali', 'Gujarati', 'Punjabi', 'Urdu', 'Nepali', 'Sinhala',
    'Spanish', 'French', 'German', 'Mandarin', 'Cantonese',
    'Japanese', 'Korean', 'Arabic', 'Portuguese', 'Italian', 'Dutch',
    'Russian', 'Polish', 'Czech', 'Greek', 'Turkish', 'Romanian',
    'Vietnamese', 'Indonesian', 'Thai', 'Filipino', 'Tagalog',
    'Hebrew', 'Swedish', 'Norwegian', 'Danish', 'Finnish',
  ];
  const re = new RegExp(`\\b(${langs.join('|')})\\b`, 'gi');
  const m = text.match(re);
  if (!m) return '';
  return Array.from(new Set(m.map((l) => l.charAt(0).toUpperCase() + l.slice(1).toLowerCase()))).join(', ');
}

function extractLocation(text, sectionValue) {
  if (sectionValue) {
    return sectionValue
      .replace(/[\n\r]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
  }
  // Without an explicit Location heading, only scan the first ~600 chars
  // (header area) for cities and cap at 2 to avoid false positives from
  // JDs that mention multiple cities as global office examples.
  const cities = [
    // India — Tier 1
    'Hyderabad', 'Bangalore', 'Bengaluru', 'Mumbai', 'Pune', 'Chennai', 'Delhi',
    'Gurgaon', 'Gurugram', 'Noida', 'Kolkata', 'Kochi', 'Ahmedabad', 'Indore',
    // India — Tier 2
    'Coimbatore', 'Trivandrum', 'Thiruvananthapuram', 'Vizag', 'Visakhapatnam',
    'Bhubaneswar', 'Nagpur', 'Jaipur', 'Lucknow', 'Chandigarh', 'Mysore',
    'Mangalore', 'Vadodara', 'Surat', 'Kanpur', 'Patna', 'Goa', 'Pondicherry',
    'Trichy', 'Madurai', 'Nashik',
    // US
    'New York', 'San Francisco', 'Seattle', 'Austin', 'Boston', 'Chicago',
    'Los Angeles', 'Denver', 'Atlanta', 'Dallas', 'Houston', 'Phoenix',
    'Miami', 'San Diego', 'Portland', 'Charlotte', 'Raleigh', 'Pittsburgh',
    'Salt Lake City', 'Washington', 'Philadelphia',
    // EU
    'London', 'Paris', 'Madrid', 'Barcelona', 'Munich', 'Frankfurt',
    'Zurich', 'Stockholm', 'Copenhagen', 'Helsinki', 'Oslo', 'Dublin',
    'Lisbon', 'Warsaw', 'Prague', 'Vienna', 'Berlin', 'Amsterdam',
    // APAC
    'Singapore', 'Hong Kong', 'Tokyo', 'Seoul', 'Manila', 'Jakarta',
    'Bangkok', 'Ho Chi Minh', 'Kuala Lumpur', 'Auckland', 'Sydney',
    'Melbourne', 'Perth', 'Brisbane',
    // ME / Africa
    'Dubai', 'Abu Dhabi', 'Doha', 'Riyadh', 'Tel Aviv', 'Cairo',
    'Johannesburg', 'Nairobi', 'Lagos',
    // Canada
    'Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa',
  ];
  const head = text.slice(0, 600);
  const found = cities.filter((c) =>
    new RegExp(`\\b${c}\\b`, 'i').test(head),
  );
  return found.slice(0, 2).join(', ');
}

function extractJobTitle(text, sections) {
  // 1) Heading-based ("Job Title:", "Position:", etc.)
  if (sections.jobTitle) {
    return cleanTitleValue(sections.jobTitle);
  }
  // 2) Intro-sentence pattern: "We are seeking/looking for/hiring a <Title>"
  //    This is the strongest signal for JDs that have no "Job Title:" label
  //    but start with prose like "We are seeking a skilled QA Automation Engineer".
  const head = (text || '').slice(0, 800);
  const intro = head.match(
    /\bwe(?:'re|\s+are)?\s+(?:currently\s+)?(?:seeking|looking\s+for|hiring|recruiting|searching\s+for|in\s+search\s+of)\s+(?:an?|the)?\s*(?:a\s+)?(?:skilled|talented|versatile|seasoned|experienced|qualified|motivated|passionate|dedicated|dynamic|creative|innovative|results[\s-]driven)?\s*([A-Z][A-Za-z][A-Za-z0-9\s/&.-]+?)(?=\s+with\b|\s+for\b|\s+who\b|\s+to\b|\s+in\b|[.,;])/i,
  );
  if (intro) {
    const candidate = cleanTitleValue(intro[1]);
    if (candidate && candidate.length >= 4 && candidate.length <= 80) {
      return candidate;
    }
  }
  // 3) First line that looks like a title (passes the heading + blacklist check)
  const lines = (text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const line of lines.slice(0, 15)) {
    if (looksLikeTitle(line)) {
      return cleanTitleValue(line);
    }
  }
  return '';
}

function cleanTitleValue(value) {
  return value
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    // Strip leading label prefixes like "Job Description of", "JD for",
    // "Hiring for", "Position:", etc., so "Job Description of Front End
    // Developer" becomes "Front End Developer".
    .replace(
      /^(?:job\s*description|job\s*title|j\.?\s?d\.?|hiring(?:\s*for)?|we\s*are\s*hiring(?:\s*for)?|opening(?:\s*for)?|position|role|designation)\s*(?:of|for|:|—|-|–)\s*/i,
      '',
    )
    // Strip trailing parenthetical like " (90%)" / " (10)" / " (remote)"
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/[,.;|].*$/, '')
    .trim()
    .slice(0, 100);
}

function extractCompanyName(text, sections) {
  if (sections.companyName) {
    return sections.companyName.split(/[\n.]/)[0].trim().slice(0, 80);
  }
  const m = text.match(/\babout\s+([A-Z][A-Za-z0-9&.\s]{2,40})\b/);
  return m ? m[1].trim() : '';
}

/* ============================================================
 * Sentence splitter (preserves common file extensions)
 * ========================================================== */

const protectedExtensions = ['.js', '.ts', '.php', '.json', '.css', '.html', '.xml', '.md'];

function splitSentencesSafely(text) {
  if (!text || typeof text !== 'string') return [];
  let temp = text;
  protectedExtensions.forEach((ext, idx) => {
    temp = temp.replace(new RegExp(`\\${ext}`, 'g'), `%%PROTECT${idx}%%`);
  });
  let sentences = temp.split(/\. ?/).map((s) => s.trim()).filter(Boolean);
  return sentences.map((line) => {
    protectedExtensions.forEach((ext, idx) => {
      line = line.replaceAll(`%%PROTECT${idx}%%`, ext);
    });
    return line.endsWith('.') ? line : line + '.';
  });
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Build nested HTML <ul><li><strong>Heading:</strong><ul><li>line.</li></ul></li></ul> */
function buildJobDescHtml(sections) {
  const order = [
    ['jobDescription', 'Job Description'],
    ['responsibilities', 'Responsibilities'],
    ['requirements', 'Requirements'],
    ['primarySkills', 'Skills'],
    ['secondarySkills', 'Nice to Have'],
    ['education', 'Education'],
    ['certifications', 'Certifications'],
    ['benefits', 'Benefits'],
  ];
  const blocks = [];
  for (const [key, label] of order) {
    const value = sections[key];
    if (!value) continue;
    const lines = splitSentencesSafely(value);
    if (!lines.length) continue;
    const items = lines.map((l) => `<li>${escapeHtml(l)}</li>`).join('');
    blocks.push(`<li><strong>${escapeHtml(label)}:</strong><ul>${items}</ul></li>`);
  }
  return blocks.length ? `<ul>${blocks.join('')}</ul>` : '';
}

/* ============================================================
 * Public API — preserved for backwards compatibility
 * ========================================================== */

/**
 * parseJD(rawText) returns a sections-object compatible with the previous
 * heading-based parser (keys are human-readable headings used by older callers).
 */
export function parseJD(rawText) {
  const sections = extractSections(rawText);
  // Re-key to legacy heading-style keys for any caller that inspects them
  const legacy = {};
  if (sections.jobTitle) legacy['Job Title'] = sections.jobTitle;
  if (sections.jobType) legacy['Job Type'] = sections.jobType;
  if (sections.location) legacy['Location'] = sections.location;
  if (sections.experience) legacy['Experience'] = sections.experience;
  if (sections.responsibilities) legacy['Responsibilities'] = sections.responsibilities;
  if (sections.requirements) legacy['Requirements'] = sections.requirements;
  if (sections.jobDescription) legacy['Job Description'] = sections.jobDescription;
  if (sections.primarySkills) legacy['Keyskills'] = sections.primarySkills;
  if (sections.secondarySkills) legacy['Keyskills - Nice to Have'] = sections.secondarySkills;
  if (sections.education) legacy['Education'] = sections.education;
  return legacy;
}

/**
 * normalizeToAIShape — the canonical entry point. Returns the same shape as
 * the OpenAI parser response so PostingsCreate.jsx can consume it directly.
 */
export function normalizeToAIShape(_legacySectionsIgnored, rawText) {
  // Re-extract sections from rawText (more reliable than legacy keys)
  const sections = extractSections(rawText || '');

  // Skills extraction strategy:
  //   - When the section text is comma/pipe-separated short chips
  //     ("React, Node.js, AWS"), trust it and split directly.
  //   - When the section text is prose-style (bullets, or any line >40 chars
  //     suggesting full sentences), run dictionary scan so only real tech
  //     terms come through instead of dumping bullet sentences as chips.
  const skillItemsFromSection = (text) => {
    if (!text) return [];
    // Split on commas / pipes / newlines only — NOT on '/' because skills
    // like ".NET / .NET Core" or "Java/Python" would split incorrectly.
    const items = text.split(/[,;|\n]+/).map((s) => s.trim()).filter(Boolean);
    const hasBullets = /[•*]/.test(text);
    const totalLen = text.length;
    const avgLen = items.length ? totalLen / items.length : 0;
    const hasLongLine = items.some((s) => s.length > 40);
    // Treat as prose if: any line >40 chars, OR total section >200 chars,
    // OR average item is wordy (>25 chars), OR bullets present.
    const isProse = hasBullets || hasLongLine || totalLen > 200 || avgLen > 25;
    if (isProse) {
      return extractSkillsFromText(text);
    }
    return items;
  };

  const primaryFromSection = skillItemsFromSection(sections.primarySkills);
  const secondaryFromSection = skillItemsFromSection(sections.secondarySkills);
  const secondarySetLower = new Set(secondaryFromSection.map((s) => s.toLowerCase()));

  // Fill gaps from whole-text scan only if primary section was missing/empty.
  // Otherwise trust the section and avoid polluting it with whole-text noise.
  const gapFill = primaryFromSection.length === 0
    ? extractSkillsFromText(rawText || '').filter(
        (s) => !secondarySetLower.has(s.toLowerCase()),
      )
    : [];
  const primaryMerged = dedupeCaseInsensitive([...primaryFromSection, ...gapFill]).slice(0, 20);

  const primarySetLower = new Set(primaryMerged.map((s) => s.toLowerCase()));
  const secondaryFiltered = secondaryFromSection.filter(
    (s) => !primarySetLower.has(s.toLowerCase()),
  );

  const expBlock = extractExperience(sections.experience || rawText || '');
  const comp = extractCompensation(sections.payBillRate || rawText || '');
  if (!comp.currency) comp.currency = extractCurrencyCode(rawText || '');

  const out = {
    jobTitle: extractJobTitle(rawText || '', sections),
    workModel: extractWorkModel(sections.workModel || rawText || ''),
    jobType: extractJobType(sections.jobType || rawText || ''),
    payBillRate: comp.payBillRate || sections.payBillRate || '',
    compensationMin: comp.compensationMin,
    compensationMax: comp.compensationMax,
    compensationType: comp.compensationType,
    currency: comp.currency,
    location: extractLocation(rawText || '', sections.location),
    primarySkills: primaryMerged.join(', '),
    secondarySkills: secondaryFiltered.join(', '),
    jobDesc: buildJobDescHtml(sections),
    externalSystemID: extractExternalSystemID(rawText || ''),
    priority: extractPriority(rawText || ''),
    experience: expBlock.experience,
    minExperience: expBlock.minExperience,
    maxExperience: expBlock.maxExperience,
    education: sections.education || extractEducation(rawText || ''),
    certifications: sections.certifications || extractCertifications(rawText || ''),
    noticePeriod: sections.noticePeriod || extractNoticePeriod(rawText || ''),
    department: sections.department || '',
    industry: sections.industry || '',
    companyName: extractCompanyName(rawText || '', sections),
    numberOfOpenings: sections.numberOfOpenings
      ? extractNumberOfOpenings(sections.numberOfOpenings) || sections.numberOfOpenings.replace(/\D/g, '')
      : extractNumberOfOpenings(rawText || ''),
    responsibilities: sections.responsibilities || '',
    benefits: sections.benefits || '',
    shiftTiming: sections.shiftTiming || extractShiftTiming(rawText || ''),
    travelRequired: sections.travelRequired || extractTravelRequired(rawText || ''),
    languagesRequired: sections.languagesRequired || extractLanguagesRequired(rawText || ''),
    reportingTo: sections.reportingTo || extractReportingTo(rawText || ''),
    teamSize: sections.teamSize || extractTeamSize(rawText || ''),
    gender: extractGender(rawText || ''),
    socialMediaJD: '',
    rawText: rawText || '',
  };

  // Final dropdown normalization
  if (!VALID_WORK_MODELS.includes(out.workModel)) out.workModel = '';
  if (out.jobType) {
    out.jobType = out.jobType
      .split(',')
      .map((s) => s.trim())
      .filter((s) => VALID_JOB_TYPES.includes(s))
      .join(',');
  }
  if (!VALID_PRIORITIES.includes(out.priority)) out.priority = '';
  if (!VALID_COMPENSATION_TYPES.includes(out.compensationType)) out.compensationType = '';

  return out;
}

function dedupeCaseInsensitive(arr) {
  const seen = new Set();
  const result = [];
  for (const item of arr) {
    const key = String(item).toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  }
  return result;
}
